// frontend/src/modules/client/CatalogModule.jsx
// ✅ VERSION CORRIGÉE - 01/12/2025
// ✅ FIX: Textarea commentaire garde le focus (plus de composants internes)
// ✅ Panier visible à droite pendant le scroll (desktop)
// ✅ Mini-panier flottant en bas (mobile)
// ✅ Choix créneau + commentaire + message prix indicatif
// ✅ UPGRADE 04/12/2025: alert() → MessageBox (success, error, warning, confirm)
import React, { useState, useEffect } from 'react';
import { ShoppingCart, User, Package, Clock, Calendar, LogOut, Plus, Minus, Info, Search, Filter, X, MapPin, MessageSquare, ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import BandeauReservation from './components/BandeauReservation';
// ✅ NOUVEAU : Import MessageBox
import { useMessageBox } from '../../components/MessageBox';

const CatalogModule = ({ user, config, onLogout, apiBase, cart, setCart, addToCart, updateCartQuantity, periodeActive }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // ✅ NOUVEAU : Hook MessageBox
  const { showMessage, showConfirm } = useMessageBox();
  
  const [produits, setProduits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState(null);
  const [showFarmInfo, setShowFarmInfo] = useState(false);
  const [loadingResa, setLoadingResa] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [loadingSilent, setLoadingSilent] = useState(false);
  
  // États pour les filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategorie, setSelectedCategorie] = useState(null);
  const [selectedOrigine, setSelectedOrigine] = useState('tous');
  const [showFilters, setShowFilters] = useState(false);

  // États pour le panier flottant
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [creneaux, setCreneaux] = useState([]);
  const [selectedCreneau, setSelectedCreneau] = useState(user?.creneau_retrait_prefere || '');
  const [commentaireClient, setCommentaireClient] = useState('');

  // Charger les créneaux de retrait
  useEffect(() => {
    const loadCreneaux = async () => {
      try {
        const response = await fetch(`${apiBase}/creneaux-retrait/actifs`);
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setCreneaux(result.data || []);
            if (user?.creneau_retrait_prefere) {
              setSelectedCreneau(user.creneau_retrait_prefere);
            } else if (result.data?.length > 0) {
              setSelectedCreneau(result.data[0].code_creneau);
            }
          }
        }
      } catch (err) {
        console.error('Erreur chargement créneaux:', err);
      }
    };
    loadCreneaux();
  }, [apiBase, user]);

  // Charger réservation en mode EDIT
  const [resaLoaded, setResaLoaded] = useState(false);
  
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const editReservationId = searchParams.get('edit');
    
    if (editReservationId && !resaLoaded) {
      console.log('🔄 MODE EDIT détecté - Réservation ID:', editReservationId);
      loadReservationForEdit(editReservationId);
      window.history.replaceState({}, '', '/catalog');
    }
  }, [location.search]);

  const loadReservationForEdit = async (reservationId, silent = false) => {
    if (resaLoaded && !silent) {
      console.log('⚠️ Réservation déjà chargée, skip');
      return;
    }
    
    try {
      setLoadingResa(true);
      console.log('📥 Chargement réservation ID:', reservationId);
      
      const response = await fetch(`${apiBase}/reservations/${reservationId}/lignes`);
      const result = await response.json();
      
      if (result.success && result.data.length > 0) {
        console.log('✅ Lignes chargées:', result.data);
        
        const produitsResponse = await fetch(`${apiBase}/produits`);
        const produitsResult = await produitsResponse.json();
        const produitsArray = produitsResult.data || produitsResult;
        
        const produitsMap = {};
        produitsArray.forEach(p => {
          produitsMap[p.code_produit] = p;
        });
        
        const cartFromResa = result.data.map(ligne => {
          const produit = produitsMap[ligne.code_produit];
          
          if (!produit) {
            console.warn('⚠️ Produit non trouvé:', ligne.code_produit);
            return null;
          }
          
          return {
            id: produit.id,
            code_produit: ligne.code_produit,
            designation: ligne.designation_produit,
            prix_ttc: parseFloat(ligne.prix_unitaire),
            unite: ligne.unite,
            quantite: parseFloat(ligne.quantite_reservee),
            image_lien: ligne.image_lien,
            quantite_disponible: parseFloat(produit.quantite_disponible) + parseFloat(ligne.quantite_reservee),
            quantite_stock_depart: produit.quantite_stock_depart,
            quantite_reservee: produit.quantite_reservee,
            categorie_id: produit.categorie_id,
            conseils_cuisson: produit.conseils_cuisson,
            recette_producteur: produit.recette_producteur,
            description: produit.description
          };
        }).filter(item => item !== null);
        
        setCart(cartFromResa);
        setResaLoaded(true);
        console.log('✅ Panier rempli avec', cartFromResa.length, 'produit(s)');
        
        if (!silent) {
          setToastMessage(`✅ Réservation chargée ! ${cartFromResa.length} produit(s) dans votre panier`);
          setTimeout(() => setToastMessage(null), 3000);
        }
        
      } else {
        console.warn('⚠️ Aucune ligne trouvée pour réservation', reservationId);
        if (!silent) {
          setToastMessage('⚠️ Réservation vide ou introuvable');
          setTimeout(() => setToastMessage(null), 3000);
        }
      }
    } catch (err) {
      console.error('❌ Erreur chargement réservation:', err);
      if (!silent) {
        setToastMessage('❌ Erreur lors du chargement');
        setTimeout(() => setToastMessage(null), 3000);
      }
    } finally {
      setLoadingResa(false);
    }
  };

  // Fonction intelligente pour ajouter au panier
  const handleAddToCart = async (produit, quantite) => {
    try {
      if (user?.code_client && periodeActive?.id) {
        const checkResponse = await fetch(`${apiBase}/reservations/client/${user.code_client}`);
        const checkResult = await checkResponse.json();
        
        if (checkResult.success && checkResult.data.length > 0) {
          const resaEnCours = checkResult.data.find(r => 
            r.periode_id === periodeActive.id && 
            ['en_cours', 'confirmee'].includes(r.statut)
          );
          
          if (resaEnCours && cart.length === 0) {
            console.log('🔄 Réservation détectée, chargement auto avant ajout...');
            setLoadingSilent(true);
            setToastMessage('🔄 Chargement de votre réservation...');
            
            await loadReservationForEdit(resaEnCours.id, true);
            await new Promise(resolve => setTimeout(resolve, 200));
            
            setToastMessage(`✅ ${produit.designation} ajouté(e) à votre réservation !`);
            setLoadingSilent(false);
            setTimeout(() => setToastMessage(null), 3000);
          }
        }
      }
      
      addToCart(produit, quantite);
      
      if (cart.length === 0 && !toastMessage) {
        setToastMessage(`✅ ${produit.designation} ajouté(e) au panier !`);
        setTimeout(() => setToastMessage(null), 2000);
      }
      
    } catch (err) {
      console.error('❌ Erreur handleAddToCart:', err);
      setToastMessage('❌ Erreur lors de l\'ajout');
      setTimeout(() => setToastMessage(null), 3000);
      addToCart(produit, quantite);
    }
  };

  // Chargement des produits
  useEffect(() => {
    const loadProduits = async () => {
      try {
        const response = await fetch(`${apiBase}/produits`);
        if (response.ok) {
          const result = await response.json();
          const produitsArray = result.data || result;
          setProduits(produitsArray);
        } else {
          throw new Error('Erreur chargement produits');
        }
      } catch (err) {
        setErrorState(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadProduits();
  }, [apiBase]);

  // Chargement des catégories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await fetch(`${apiBase}/categories`);
        if (response.ok) {
          const result = await response.json();
          const categoriesArray = result.data || result;
          setCategories(categoriesArray);
        }
      } catch (err) {
        console.error('Erreur chargement catégories:', err);
      }
    };

    loadCategories();
  }, [apiBase]);

  // Filtrage des produits
  const getFilteredProduits = () => {
    return produits.filter(produit => {
      const matchSearch = searchTerm === '' || 
        produit.designation.toLowerCase().includes(searchTerm.toLowerCase());
      const matchCategorie = selectedCategorie === null || 
        produit.categorie_id === selectedCategorie;
      const matchOrigine = selectedOrigine === 'tous' || 
        produit.origine === selectedOrigine;
      return matchSearch && matchCategorie && matchOrigine;
    });
  };

  // Calcul total panier
  const getTotalCart = () => {
    return cart.reduce((total, item) => total + (Number(item.prix_ttc) * item.quantite), 0);
  };

  // Nombre total d'articles
  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantite, 0);
  };

  // ✅ Fonction de réservation avec créneau et commentaire - CORRIGÉE avec MessageBox
  const handleReservation = async () => {
    if (cart.length === 0) return;
    
    if (!periodeActive) {
      // ✅ REMPLACÉ : alert() → warning()
      showMessage('warning', 'Aucune période de réservation active');
      return;
    }

    if (!selectedCreneau) {
      // ✅ REMPLACÉ : alert() → warning()
      showMessage('warning', 'Veuillez sélectionner un créneau de retrait');
      return;
    }

    try {
      const checkResponse = await fetch(`${apiBase}/reservations/client/${user.code_client}`);
      const checkResult = await checkResponse.json();
      
      if (checkResult.success && checkResult.data.length > 0) {
        const resaEnCours = checkResult.data.find(r => 
          r.periode_id === periodeActive.id && 
          ['en_cours', 'confirmee'].includes(r.statut)
        );
        
        if (resaEnCours) {
          // ✅ REMPLACÉ : window.confirm() → showConfirm()
          const confirmResult = await showConfirm(
            `⚠️ ATTENTION\n\n` +
            `Vous avez déjà une réservation en cours :\n` +
            `${resaEnCours.numero_reservation}\n` +
            `Montant : ${Number(resaEnCours.montant_estime).toFixed(2)}€\n\n` +
            `En confirmant, vous REMPLACEREZ votre réservation existante.\n\n` +
            `Voulez-vous quand même continuer ?`,
            'warning'
          );
          
          if (!confirmResult) {
            console.log('❌ Écrasement réservation annulé par l\'utilisateur');
            return;
          }
        }
      }
      
      setLoading(true);
      
      const reservationData = {
        code_client: user.code_client,
        periode_id: periodeActive.id,
        creneau_retrait_code: selectedCreneau,
        notes_client: commentaireClient.trim() || null,
        produits: cart.map(item => ({
          code_produit: item.code_produit,
          quantite: item.quantite
        }))
      };

      const response = await fetch(`${apiBase}/reservations/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reservationData)
      });

      const result = await response.json();
     
      if (result.success) {
        const now = new Date();
        const timestamp = now.toLocaleString('fr-FR', {
          day: '2-digit',
          month: '2-digit', 
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        
        const creneauInfo = creneaux.find(c => c.code_creneau === selectedCreneau);
        
        const message = result.is_update 
          ? `✅ Réservation modifiée !\n\n` +
            `Numéro: ${result.numero_reservation}\n` +
            `Total: ${getTotalCart().toFixed(2)}€\n` +
            `Retrait: ${creneauInfo?.libelle || selectedCreneau}\n\n` +
            `📧 Notification envoyée au fermier\n` +
            `⏱️ ${timestamp}`
          : `📝 Demande de réservation transmise !\n\n` +
            `Numéro: ${result.numero_reservation}\n` +
            `Total estimé: ${getTotalCart().toFixed(2)}€\n` +
            `Retrait: ${creneauInfo?.libelle || selectedCreneau}\n\n` +
            `⏱️ ${timestamp}\n\n` +
            `💡 Le fermier va examiner votre demande.`;
        
        // ✅ REMPLACÉ : alert() → showMessage()
        showMessage('success', message);

        setCart([]);
        setCommentaireClient('');
  
        const produitsRes = await fetch(`${apiBase}/produits`);
        if (produitsRes.ok) {
          const produitsResult = await produitsRes.json();
          const produitsArray = produitsResult.data || produitsResult;
          setProduits(produitsArray);
        }
        
        navigate('/profile');
        
      } else {
        throw new Error(result.error || 'Erreur lors de la réservation');
      }      

    } catch (err) {
      // ✅ REMPLACÉ : alert() → showMessage()
      showMessage('error', 'Erreur: ' + err.message);
      console.error('Erreur réservation:', err);
    } finally {
      setLoading(false);
    }
  };

  // Affichage chargement / erreur
  if (loading && produits.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Chargement du catalogue...</p>
        </div>
      </div>
    );
  }

  if (errorState) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg max-w-md text-center">
          <h3 className="font-bold text-lg mb-2">Erreur de chargement</h3>
          <p>{errorState}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const produitsFiltered = getFilteredProduits();

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU PRINCIPAL
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast message */}
      {toastMessage && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg">
          {toastMessage}
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Package className="h-8 w-8 text-green-600" />
              <div>
                <h1 className="text-xl font-bold text-green-800">
                  {config?.nom_ferme || 'Catalogue'}
                </h1>
                <p className="text-sm text-gray-600">Réservation de produits fermiers</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Bouton info ferme */}
              <button
                onClick={() => setShowFarmInfo(true)}
                className="text-green-600 hover:text-green-800"
                title="Informations ferme"
              >
                <Info className="h-5 w-5" />
              </button>
              
              {/* Bouton profil */}
              <button
                onClick={() => navigate('/profile')}
                className="flex items-center gap-2 text-gray-600 hover:text-green-600"
              >
                <User className="h-5 w-5" />
                <span className="hidden sm:inline">{user?.nom_prenom?.split(' ')[0]}</span>
              </button>
              
              {/* Bouton déconnexion */}
              <button
                onClick={onLogout}
                className="text-gray-500 hover:text-red-600"
                title="Déconnexion"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bandeau réservation */}
      <BandeauReservation periodeActive={periodeActive} />

      {/* Barre de recherche et filtres */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Recherche */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un produit..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            
            {/* Bouton filtres (mobile) */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="sm:hidden flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg"
            >
              <Filter className="h-5 w-5" />
              Filtres
            </button>
            
            {/* Filtres (desktop) */}
            <div className="hidden sm:flex gap-3">
              <select
                value={selectedCategorie || ''}
                onChange={(e) => setSelectedCategorie(e.target.value ? parseInt(e.target.value) : null)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="">Toutes catégories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.nom}</option>
                ))}
              </select>
              
              <select
                value={selectedOrigine}
                onChange={(e) => setSelectedOrigine(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="tous">Toutes origines</option>
                <option value="ferme">Produits de la ferme</option>
                <option value="externe">Partenaires</option>
              </select>
            </div>
          </div>
          
          {/* Filtres (mobile - expanded) */}
          {showFilters && (
            <div className="sm:hidden mt-3 flex flex-col gap-3">
              <select
                value={selectedCategorie || ''}
                onChange={(e) => setSelectedCategorie(e.target.value ? parseInt(e.target.value) : null)}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Toutes catégories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.nom}</option>
                ))}
              </select>
              
              <select
                value={selectedOrigine}
                onChange={(e) => setSelectedOrigine(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="tous">Toutes origines</option>
                <option value="ferme">Produits de la ferme</option>
                <option value="externe">Partenaires</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Contenu principal avec sidebar panier */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Liste des produits */}
          <div className="flex-1">
            {produitsFiltered.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600">Aucun produit trouvé</h3>
                <p className="text-gray-500">Essayez de modifier vos critères de recherche</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {produitsFiltered.map(produit => {
                  const inCart = cart.find(item => item.id === produit.id);
                  const qteDisponible = produit.quantite_disponible || 0;
                  const isDisponible = qteDisponible > 0;
                  
                  return (
                    <div 
                      key={produit.id}
                      className={`bg-white rounded-lg shadow-sm border overflow-hidden ${!isDisponible ? 'opacity-60' : ''}`}
                    >
                      {/* Image */}
                      <div className="h-40 bg-gray-100 relative">
                        {produit.image_lien ? (
                          <img 
                            src={produit.image_lien} 
                            alt={produit.designation}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="h-12 w-12 text-gray-300" />
                          </div>
                        )}
                        
                        {/* Badge origine */}
                        {produit.origine === 'ferme' && (
                          <span className="absolute top-2 left-2 bg-green-600 text-white text-xs px-2 py-1 rounded-full">
                            🌱 Ferme
                          </span>
                        )}
                        
                        {/* Badge rupture */}
                        {!isDisponible && (
                          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                            <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                              Épuisé
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Contenu */}
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 mb-1">{produit.designation}</h3>
                        
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-lg font-bold text-green-600">
                            {Number(produit.prix_ttc).toFixed(2)}€
                            <span className="text-sm font-normal text-gray-500">/{produit.unite}</span>
                          </span>
                          
                          {isDisponible && (
                            <span className="text-xs text-gray-500">
                              Dispo: {qteDisponible} {produit.unite}
                            </span>
                          )}
                        </div>
                        
                        {/* Boutons action */}
                        {isDisponible && (
                          <div className="flex items-center gap-2">
                            {inCart ? (
                              <>
                                <button
                                  onClick={() => updateCartQuantity(produit.id, inCart.quantite - 1)}
                                  className="bg-gray-200 w-10 h-10 rounded-lg flex items-center justify-center hover:bg-gray-300"
                                >
                                  <Minus className="h-4 w-4" />
                                </button>
                                <span className="font-bold text-lg w-12 text-center">{inCart.quantite}</span>
                                <button
                                  onClick={() => updateCartQuantity(produit.id, inCart.quantite + 1)}
                                  disabled={inCart.quantite >= qteDisponible}
                                  className="bg-green-600 text-white w-10 h-10 rounded-lg flex items-center justify-center hover:bg-green-700 disabled:bg-gray-400"
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => handleAddToCart(produit, 1)}
                                className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2"
                              >
                                <Plus className="h-4 w-4" />
                                Ajouter
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ═══════════════════════════════════════════════════════════════════════════ */}
          {/* SIDEBAR PANIER (Desktop) */}
          {/* ═══════════════════════════════════════════════════════════════════════════ */}
          <div className="hidden lg:block w-96">
            <div className="bg-white rounded-lg shadow-lg sticky top-24">
              <div className="bg-green-600 text-white p-4 rounded-t-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5" />
                    <span className="font-bold">Mon Panier</span>
                  </div>
                  <span className="bg-white text-green-600 px-2 py-1 rounded-full text-sm font-bold">
                    {getTotalItems()}
                  </span>
                </div>
              </div>
              
              {cart.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <ShoppingCart className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>Votre panier est vide</p>
                </div>
              ) : (
                <div className="max-h-[60vh] overflow-y-auto">
                  {/* Liste produits panier */}
                  <div className="divide-y">
                    {cart.map((item) => (
                      <div key={item.id} className="p-3 flex items-center justify-between">
                        <div className="flex-1">
                          <span className="font-medium text-gray-900 text-sm">{item.designation}</span>
                          <div className="flex items-center gap-2 mt-1">
                            <button
                              onClick={() => updateCartQuantity(item.id, item.quantite - 1)}
                              className="bg-gray-200 w-6 h-6 rounded flex items-center justify-center hover:bg-gray-300"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="font-bold w-8 text-center">{item.quantite}</span>
                            <button
                              onClick={() => updateCartQuantity(item.id, item.quantite + 1)}
                              disabled={item.quantite >= item.quantite_disponible}
                              className="bg-gray-200 disabled:opacity-50 w-6 h-6 rounded flex items-center justify-center hover:bg-gray-300"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-green-600">{(item.prix_ttc * item.quantite).toFixed(2)}€</span>
                          <button
                            onClick={() => updateCartQuantity(item.id, 0)}
                            className="block text-red-500 hover:text-red-700 text-xs mt-1"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Message prix indicatif */}
                  <div className="p-3 bg-amber-50 border-t border-amber-200">
                    <p className="text-xs text-amber-800 flex items-center gap-1">
                      <Info className="h-4 w-4 flex-shrink-0" />
                      Prix indicatif. Montant final selon poids réel à la pesée.
                    </p>
                  </div>

                  {/* Créneau de retrait */}
                  <div className="p-3 border-t">
                    <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Créneau de retrait
                    </label>
                    <select
                      value={selectedCreneau}
                      onChange={(e) => setSelectedCreneau(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">-- Choisir un créneau --</option>
                      {creneaux.map(creneau => (
                        <option key={creneau.id} value={creneau.code_creneau}>
                          {creneau.libelle} ({creneau.heure_debut?.substring(0,5)}-{creneau.heure_fin?.substring(0,5)})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Commentaire client */}
                  <div className="p-3 border-t">
                    <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Précisions (facultatif)
                    </label>
                    <textarea
                      value={commentaireClient}
                      onChange={(e) => setCommentaireClient(e.target.value)}
                      placeholder="Ex: Tomates bien mûres, courgettes petites..."
                      rows={2}
                      className="w-full px-3 py-2 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  {/* Total et bouton validation */}
                  <div className="p-4 bg-gray-50 border-t">
                    <div className="flex justify-between items-center mb-4">
                      <span className="font-medium text-gray-700">Total estimé:</span>
                      <span className="text-2xl font-bold text-green-600">{getTotalCart().toFixed(2)}€</span>
                    </div>
                    
                    <button
                      onClick={handleReservation}
                      disabled={loading || !selectedCreneau}
                      className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <ShoppingCart className="h-5 w-5" />
                          Valider ma réservation
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* MINI PANIER MOBILE - INLINE (pas de composant interne) */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {cart.length > 0 && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50">
          {/* Panneau étendu */}
          {mobileCartOpen && (
            <div className="bg-white border-t shadow-2xl max-h-[70vh] overflow-y-auto">
              {/* Liste produits */}
              <div className="divide-y">
                {cart.map((item) => (
                  <div key={item.id} className="p-3 flex items-center justify-between">
                    <div className="flex-1">
                      <span className="font-medium text-gray-900 text-sm">{item.designation}</span>
                      <div className="flex items-center gap-2 mt-1">
                        <button
                          onClick={() => updateCartQuantity(item.id, item.quantite - 1)}
                          className="bg-gray-200 w-6 h-6 rounded flex items-center justify-center"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="font-bold w-6 text-center">{item.quantite}</span>
                        <button
                          onClick={() => updateCartQuantity(item.id, item.quantite + 1)}
                          disabled={item.quantite >= item.quantite_disponible}
                          className="bg-gray-200 disabled:opacity-50 w-6 h-6 rounded flex items-center justify-center"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-green-600">{(item.prix_ttc * item.quantite).toFixed(2)}€</span>
                      <button
                        onClick={() => updateCartQuantity(item.id, 0)}
                        className="block text-red-500 text-xs mt-1"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Message prix indicatif */}
              <div className="p-3 bg-amber-50 border-t border-amber-200">
                <p className="text-xs text-amber-800 flex items-center gap-1">
                  <Info className="h-4 w-4" />
                  Prix indicatif. Montant final selon poids réel.
                </p>
              </div>

              {/* Créneau */}
              <div className="p-3 border-t">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Créneau de retrait</label>
                <select
                  value={selectedCreneau}
                  onChange={(e) => setSelectedCreneau(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="">-- Choisir --</option>
                  {creneaux.map(creneau => (
                    <option key={creneau.id} value={creneau.code_creneau}>
                      {creneau.libelle} ({creneau.heure_debut}-{creneau.heure_fin})
                    </option>
                  ))}
                </select>
              </div>

              {/* ✅ FIX: Commentaire - textarea inline */}
              <div className="p-3 border-t">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Précisions</label>
                <textarea
                  value={commentaireClient}
                  onChange={(e) => setCommentaireClient(e.target.value)}
                  placeholder="Ex: Tomates mûres, courgettes petites..."
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg text-sm resize-none"
                />
              </div>
            </div>
          )}

          {/* Barre fixe en bas */}
          <div className="bg-green-600 text-white p-3 shadow-lg">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setMobileCartOpen(!mobileCartOpen)}
                className="flex items-center gap-2"
              >
                <div className="relative">
                  <ShoppingCart className="h-6 w-6" />
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                    {getTotalItems()}
                  </span>
                </div>
                <span className="font-bold text-lg">{getTotalCart().toFixed(2)}€</span>
                {mobileCartOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
              </button>

              <button
                onClick={handleReservation}
                disabled={loading || !selectedCreneau}
                className="bg-white text-green-600 px-4 py-2 rounded-lg font-bold disabled:opacity-50"
              >
                {loading ? '...' : 'Valider'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal informations ferme */}
      {showFarmInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="bg-green-600 p-6 text-center">
              <h2 className="text-xl font-bold text-white">
                {config?.nom_ferme || 'La Ferme du Petit Essart'}
              </h2>
            </div>
            
            <div className="p-6 space-y-4">
              {config?.adresse && (
                <div className="flex items-start gap-3">
                  <div>
                    <h3 className="font-medium text-gray-900">Adresse</h3>
                    <p className="text-gray-600">{config.adresse}</p>
                  </div>
                </div>
              )}
              
              {config?.telephone && (
                <div className="flex items-center gap-3">
                  <div>
                    <h3 className="font-medium text-gray-900">Téléphone</h3>
                    <p className="text-gray-600">{config.telephone}</p>
                  </div>
                </div>
              )}
              
              {config?.lien_decouverte && (
                <a 
                  href={config.lien_decouverte} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block w-full bg-green-100 text-green-800 py-3 rounded-lg text-center font-medium hover:bg-green-200 transition-colors"
                >
                  Découvrir la ferme
                </a>
              )}
            </div>
            
            <div className="border-t p-4">
              <button
                onClick={() => setShowFarmInfo(false)}
                className="w-full bg-gray-100 text-gray-800 py-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatalogModule;